import { Component, inject, Input, OnInit } from '@angular/core';
import { IonicModule, ModalController, ToastController } from '@ionic/angular';

import { AuthStateService, Show } from '../auth/auth-state.service';
import { ApiService } from '../../services/api.service';
import { ShowAvatarComponent } from '../../components/show-avatar/show-avatar.component';

/**
 * Modal component for picking the active show. Used in two flows:
 *  - First-time pick when a multi-show user has no active show set yet.
 *  - Voluntary switcher from the side menu where an active show
 *    already exists. The active row is marked with a check icon so the
 *    user can see "this is my current one" at a glance.
 *
 * Tap a row to switch — there's no separate confirm step. Tapping the
 * row that's already active just dismisses the modal (no-op). An
 * in-flight switch disables further taps so a second fat-finger tap
 * doesn't fire two API calls.
 */
@Component({
    selector: 'app-select-show-modal',
    templateUrl: './select-show-modal.component.html',
    styleUrls: ['./select-show-modal.component.scss'],
    imports: [
    IonicModule,
    ShowAvatarComponent
]
})
export class SelectShowModalComponent implements OnInit {

    @Input() shows: Show[] = [];

    /** Id of the row currently being switched to, or null if idle. */
    public switchingShowId: number | null = null;
    public sortedShows: Show[] = [];

    private modalController: ModalController = inject(ModalController);
    private authStateService: AuthStateService = inject(AuthStateService);
    private apiService: ApiService = inject(ApiService);
    private toastController: ToastController = inject(ToastController);

    public get isSwitching(): boolean {
        return this.switchingShowId !== null;
    }

    ngOnInit(): void {
        console.log('[SelectShowModal] Initialized with shows:', this.shows);
        // Published shows first, then everything else, alphabetical within
        // each group. Don't mutate the @Input reference.
        this.sortedShows = [...this.shows].sort((a, b) => {
            const aPublished = a.status === 'PUBLISHED' ? 0 : 1;
            const bPublished = b.status === 'PUBLISHED' ? 0 : 1;
            if (aPublished !== bPublished) {
                return aPublished - bPublished;
            }
            return a.name.localeCompare(b.name);
        });
    }

    /**
     * True when the given show is the user's current active show.
     * Drives the check-icon indicator. Read directly from auth state so
     * the modal stays in sync with whatever else might have changed it
     * (e.g. an admin edit elsewhere in the app).
     */
    public isCurrentlyActive(show: Show): boolean {
        return this.authStateService.activeShow()?.id === show.id;
    }

    /**
     * Tap handler for a show row. If the tapped row is already the
     * active show, dismiss without an API call (the user just confirmed
     * "yes, stay where I am"). Otherwise persist the new active show
     * to the BE, mirror it into auth state, and dismiss — the parent
     * (app.component) reloads the page on a confirm dismissal.
     */
    public async pickShow(show: Show): Promise<void> {
        if (this.isSwitching) {
            return;
        }

        if (this.isCurrentlyActive(show)) {
            await this.modalController.dismiss(null, 'cancel');
            return;
        }

        this.switchingShowId = show.id;

        try {
            // Persist the choice on the server FIRST — the post-reload
            // /users/me call reads the active show from the backend, so
            // without this the UI briefly shows the new show and then
            // snaps back to the old one after reload.
            await this.apiService.setActiveShow(show.id);

            // Update local state so callers (e.g. app.component) can react
            // before the page reload takes over.
            this.authStateService.setActiveShow(show);
            console.log('[SelectShowModal] Active show set to:', show);

            await this.modalController.dismiss(show, 'confirm');
        } catch (error) {
            console.error('[SelectShowModal] Error setting active show:', error);
            this.switchingShowId = null;
            const toast = await this.toastController.create({
                message: 'Kon de actieve show niet wijzigen. Probeer het opnieuw.',
                color: 'danger',
                duration: 3000
            });
            await toast.present();
        }
    }
}
